import type { CardProps } from '@mui/material';
import { Divider, CircularProgress } from '@mui/material';
import { forwardRef } from 'react';
import { Card } from '../../Layout/Card';
import { Box } from '../../Layout/Box';
import { Typography } from '../../Typography';

export interface WidgetProps extends Omit<CardProps, 'variant'> {
  /**
   * Card variant. Explicitly set so that spread props cannot override it.
   * @default 'outlined'
   */
  variant?: 'outlined' | 'elevation';
  /**
   * The title of the widget displayed in the header.
   */
  title?: string;
  /**
   * Optional subtitle displayed below the title.
   */
  subtitle?: string;
  /**
   * Action element displayed in the header (e.g., close icon, menu button).
   */
  action?: React.ReactNode;
  /**
   * Footer content area displayed at the bottom of the widget.
   */
  footer?: React.ReactNode;
  /**
   * If `true`, displays a loading overlay with a spinner.
   * @default false
   */
  loading?: boolean;
  /**
   * If `true`, displays a divider between the header and content.
   * @default false
   */
  divider?: boolean;
  /**
   * If `true`, applies padding to the header.
   * @default true
   */
  headerPadding?: boolean;
  /**
   * If `true`, applies padding to the content area.
   * @default false
   */
  contentPadding?: boolean;
  /**
   * The content of the widget.
   */
  children?: React.ReactNode;
}

// Internalized styles based on previous sharedStyles['widget-container']
const getVariantStyles = () => ({
  marginLeft: 2,
  marginRight: 2,
  marginBottom: 2,
  borderRadius: '12px',
  boxShadow: 1,
});

const headerStyles = {
  padding: '16px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  gap: 2,
};

const headerContentStyles = {
  display: 'flex',
  flexDirection: 'column',
  gap: 0.5,
  flexGrow: 1,
};

const loadingOverlayStyles = {
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundColor: 'rgba(255, 255, 255, 0.7)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1,
};

const footerStyles = {
  padding: '16px',
  borderTop: 1,
  borderColor: 'divider',
};

/**
 * Widget component - A flexible card-based container for displaying content with optional header, footer, and actions.
 *
 * @example
 * ```tsx
 * <Widget title="My Widget" subtitle="Subtitle text">
 *   Content goes here
 * </Widget>
 * ```
 *
 * @example
 * ```tsx
 * <Widget
 *   title="Widget with Actions"
 *   action={<CloseButton onClick={onClose} />}
 *   footer={<Button>Action</Button>}
 *   loading={isLoading}
 * >
 *   Content
 * </Widget>
 * ```
 */
export const Widget = forwardRef<HTMLDivElement, WidgetProps>(
  (
    {
      variant = 'outlined',
      title,
      subtitle,
      action,
      footer,
      loading = false,
      divider = false,
      headerPadding = true,
      contentPadding = false,
      children,
      sx,
      ...other
    },
    ref
  ) => {
    const hasHeader = title || subtitle || action;

    return (
      <Card
        ref={ref}
        data-testid="widget-card"
        variant={variant}
        sx={[getVariantStyles(), ...(Array.isArray(sx) ? sx : [sx])]}
        {...other}
      >
        {/* Loading Overlay */}
        {loading && (
          <Box sx={loadingOverlayStyles}>
            <CircularProgress />
          </Box>
        )}

        {/* Header */}
        {hasHeader && (
          <Box sx={headerPadding ? headerStyles : { ...headerStyles, padding: 0 }}>
            <Box sx={headerContentStyles}>
              {title && (
                <Typography variant="h2" component="h2">
                  {title}
                </Typography>
              )}
              {subtitle && (
                <Typography variant="caption" color="text.secondary">
                  {subtitle}
                </Typography>
              )}
            </Box>
            {action && <Box sx={{ flexShrink: 0 }}>{action}</Box>}
          </Box>
        )}

        {/* Divider */}
        {hasHeader && divider && <Divider />}

        {/* Content */}
        {children && <Box sx={contentPadding ? { padding: '16px' } : undefined}>{children}</Box>}

        {/* Footer */}
        {footer && <Box sx={footerStyles}>{footer}</Box>}
      </Card>
    );
  }
);

Widget.displayName = 'Widget';
